import { Fragment, type ReactNode } from 'react';

/** Escape regex special chars in a token so user input can't break the matcher. */
function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Split `text` on case-insensitive matches of any token in `query` and wrap
 * each match in a `<mark>`. Whitespace in `query` splits into multiple
 * independent tokens so "leaf design" highlights both "leaf" and "design".
 *
 * Returns a flat ReactNode[] suitable for `{children}` rendering. Returns
 * the original string in a single-element array when there's no query or
 * no match — caller doesn't need to special-case empty results.
 *
 * Defensive against:
 * - empty / whitespace-only query (no highlights, returns [text])
 * - regex special chars in query (escaped)
 * - empty text (returns [text])
 */
export function highlightMatch(text: string, query: string): ReactNode[] {
  if (!text) return [text];
  const tokens = query
    .split(/\s+/)
    .map((t) => t.trim())
    .filter((t) => t.length > 0)
    .map(escapeRegExp);
  if (tokens.length === 0) return [text];

  const pattern = new RegExp(`(${tokens.join('|')})`, 'gi');
  const parts = text.split(pattern);

  const out: ReactNode[] = [];
  let idx = 0;
  for (const part of parts) {
    if (part.length === 0) continue;
    if (pattern.test(part)) {
      pattern.lastIndex = 0; // reset for next test() call
      out.push(<mark key={idx++}>{part}</mark>);
    } else {
      out.push(<Fragment key={idx++}>{part}</Fragment>);
    }
  }
  return out;
}
